import { nextTick, type WritableComputedRef } from 'vue'
import type { I18n, UseI18nOptions } from 'vue-i18n'
import { createI18n, useI18n as useI18nDefault } from 'vue-i18n'

import { V1_PROGRAMS_QUERY_KEY } from '@/features/programs/api'
import programsStore from '@/features/programs/stores/programs/programs.store'
import type { V1ProgramType } from '@/features/programs/types'
import { setUserLanguage } from '@/lib/posthog'
import queryClient from '@/lib/vue-query'
import type { TranslationsFileType } from '@/locales'
// import { i18nInstance } from '@/main' // Éviter référence circulaire
import { I18N_DEFAULT_LOCALE, I18N_LOCAL_STORAGE_KEY } from '@/utils/config/constants'

export const SUPPORT_LOCALES = ['en', 'fr']

export type SupportedLocalesType = (typeof SUPPORT_LOCALES)[number]
export type TranslationKeysType = keyof TranslationsFileType

export async function setupI18n() {
  // Charger les messages français par défaut
  const defaultMessages = await import(`../locales/fr`)
  
  const i18n = createI18n({
    locale: I18N_DEFAULT_LOCALE,
    fallbackLocale: 'fr',
    legacy: false,
    warnHtmlMessage: false,
    messages: {
      fr: defaultMessages.default
    },
    postTranslation: (value) => {
      let newValue = value as string

      try {
        const i18nMenteeFallbackName = 'mentee'
        const i18nMentorFallbackName = 'mentor'

        // get program data
        const programs: V1ProgramType[] | undefined = queryClient.getQueryData([V1_PROGRAMS_QUERY_KEY])
        const selectedProgramId = programsStore.selectedProgramId
        const program = programs?.find((p) => p.id === selectedProgramId)
        if (program) {
          const programSettings = program.settings
          const i18nMenteeSettingName = programSettings.find((setting) => setting.name === 'I18N_MENTEE_NAME')?.value
          const i18nMentorSettingName = programSettings.find((setting) => setting.name === 'I18N_MENTOR_NAME')?.value

          if (String(newValue).includes('%*mentee*%')) {
            if (i18nMenteeSettingName) {
              // use the setting if it exists
              newValue = String(newValue).replaceAll('%*mentee*%', i18nMenteeSettingName)
            }
          }
          if (String(newValue).includes('%*mentor*%')) {
            // use the setting if it exists
            if (i18nMentorSettingName) {
              newValue = String(newValue).replaceAll('%*mentor*%', i18nMentorSettingName)
            }
          }
        }

        // if the program hasn't loaded yet or the program doesn't have a setting, use the fallback
        if (String(newValue).includes('%*mentee*%')) {
          newValue = String(newValue).replaceAll('%*mentee*%', i18nMenteeFallbackName)
        }
        if (String(newValue).includes('%*mentor*%')) {
          newValue = String(newValue).replaceAll('%*mentor*%', i18nMentorFallbackName)
        }

        // uppercase the first letter
        newValue = newValue.charAt(0).toUpperCase() + newValue.slice(1)

        // return value
        return newValue
      } catch (error) {
        return value as string
      }
    },
  })
  return i18n
}

// set i18n language
export function setI18nLanguage(i18n: I18n, locale: SupportedLocalesType) {
  // `I18n` non paramétré laisse `global.locale` en `string | WritableComputedRef`
  // (mode legacy ou composition) ; l'instance est créée avec `legacy: false`
  // (cf. createI18n ci-dessus), donc c'est toujours un ref inscriptible.
  ;(i18n.global.locale as WritableComputedRef<string>).value = locale
  document.querySelector('html')?.setAttribute('lang', locale)
  window.localStorage.setItem(I18N_LOCAL_STORAGE_KEY, locale)
  setUserLanguage(locale)
}

export function getLocaleFromLocalStorage() {
  // get local from local storage
  let paramsLocal = window.localStorage.getItem(I18N_LOCAL_STORAGE_KEY)
  if (!paramsLocal) {
    paramsLocal = navigator.language.split('-')[0]
  }
  return paramsLocal
}

export async function loadLocaleMessages(i18n: I18n, locale: SupportedLocalesType) {
  const localeToLoad = SUPPORT_LOCALES.includes(locale) ? locale : 'en'
  // load locale messages with dynamic import
  const messages = await import(`../locales/${localeToLoad}.ts`)

  // set locale and locale message
  i18n.global.setLocaleMessage(localeToLoad, messages.default)

  return nextTick()
}

export async function switchI18nLanguage(i18n: I18n, locale: SupportedLocalesType) {
  await loadLocaleMessages(i18n, locale)
  setI18nLanguage(i18n, locale)
}

interface I18nOptionsType extends UseI18nOptions {
  messages?: Record<string, TranslationsFileType>
}

// Global i18n instance holder
let globalI18nInstance: I18n | null = null

export function setGlobalI18nInstance(instance: I18n) {
  globalI18nInstance = instance
}

export function getGlobalI18nInstance(): I18n | null {
  return globalI18nInstance
}

export function useI18n(options?: I18nOptionsType | undefined) {
  const I18nComposition = useI18nDefault<I18nOptionsType>(options)

  return {
    ...I18nComposition,
    i18nGlobal: globalI18nInstance
  }
}

export type I18nTType = ReturnType<typeof useI18n>['t']
